import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { useAuth } from "./AuthContext";

type JourneyState = {
	user_id: number;
	current_stage: string;
	last_route: string;
	meta: Record<string, unknown>;
	updated_at: string | null;
	available_stages: string[];
	checkpoint_template: string[][];
	current_checkpoint_index: number;
	current_checkpoint: string[];
	/** DEBUG: LenderBAPJourney.pk currently bound to this user (null if not yet set) */
	journey_id: number | null;
};

type JourneyEventPayload = {
	stage?: string;
	route?: string;
	event_type?: "page_view" | "stage_advance" | "custom";
	payload?: Record<string, unknown>;
	advance?: boolean;
	allow_rewind?: boolean;
};

type JourneyContextType = {
	state: JourneyState | null;
	loading: boolean;
	refresh: () => Promise<void>;
	logEvent: (payload: JourneyEventPayload) => Promise<any>;
};

const JourneyContext = createContext<JourneyContextType | undefined>(undefined);

/**
 * The Journey APIs (/api/journey/state/, /api/journey/event/) belong to
 * primary_app and expect request.user to be a django.contrib.auth.models.User
 * (the loan-application journey). They must NEVER be called while the
 * browser is on an LMS route (/lms/*), because on those routes the only
 * valid session is an LMS `Customer` (a completely separate model,
 * authenticated via lms.backends.CustomerBackend). Calling these endpoints
 * from an LMS page previously caused a 500 error server-side
 * (LoanJourneyState.objects.get_or_create(user=<Customer instance>) raises
 * ValueError: Cannot query "...": Must be "User" instance) because the
 * session's authenticated principal was an LMS Customer, not a primary_app
 * User.
 *
 * JourneyProvider is mounted above BrowserRouter (see main.tsx), so it has
 * no react-router location context — we check window.location.pathname
 * directly instead.
 */
const isLmsRoute = () =>
	typeof window !== "undefined" && window.location.pathname.startsWith("/lms");

export const JourneyProvider = ({ children }: { children: ReactNode }) => {
	const { user } = useAuth();
	const [state, setState] = useState<JourneyState | null>(null);
	const [loading, setLoading] = useState(false);

	// Deduplicate requests using a ref to store the active promise
	const fetchPromiseRef = useRef<Promise<void> | null>(null);

	const fetchState = useCallback(async () => {
		if (!user || isLmsRoute()) {
			setState(null);
			setLoading(false);
			return;
		}

		// If a request is already in progress, return the existing promise
		if (fetchPromiseRef.current) {
			return fetchPromiseRef.current;
		}

		setLoading(true);

		const promise = (async () => {
			try {
				const res = await axios.post(`${API_BASE_URL}/api/journey/state/`);
				setState(res.data);
			} catch (err) {
				console.error("Failed to fetch journey state", err);
				setState(null);
			} finally {
				// Clear the promise ref and loading state
				fetchPromiseRef.current = null;
				setLoading(false);
			}
		})();

		fetchPromiseRef.current = promise;
		return promise;
	}, [user]);

	useEffect(() => {
		fetchState();
	}, [fetchState]);

	const logEvent = useCallback(
		async (payload: JourneyEventPayload) => {
			if (!user || isLmsRoute()) return;
			try {
				const res = await axios.post(`${API_BASE_URL}/api/journey/event/`, payload);
				return res.data;
			} catch (err) {
				console.error("Failed to log journey event", err);
				throw err;
			}
		},
		[user]
	);

	const value = useMemo(
		() => ({
			state,
			loading,
			refresh: fetchState,
			logEvent,
		}),
		[state, loading, fetchState, logEvent]
	);

	return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
};

export const useJourney = () => {
	const ctx = useContext(JourneyContext);
	if (!ctx) {
		throw new Error("useJourney must be used within JourneyProvider");
	}
	return ctx;
};


